<template>
  <div>
    <b-loading :active="loading" />

    <div class="bg-white px-4">
      <section
        class="cursor-pointer flex items-center pt-4 text-black text-xl"
        @click.prevent="toggleOrderDetail"
      >
        <span class="font-medium mr-2">{{ orderInfo.name }}</span>
        <i
          :class="showOrderDetail ? 'icon-up-chevron' : 'icon-down-chevron'"
          class="text-gray-500"
        />
      </section>
      <transition name="fade">
        <div v-if="showOrderDetail" class="flex flex-wrap gap-4 py-4">
          <section id="summary" class="order__info-card min-w-[452px]">
            <div class="order__info-item center">
              <label class="order__info-item__label">
                總金額
              </label>
              <p class="order__info-item__content text-blue-500">
                {{ orderStatistic.total | commaFormat | priceFormat }}
              </p>
            </div>
            <div class="order__info-item center">
              <label class="order__info-item__label">
                品項總數量
              </label>
              <p class="order__info-item__content">
                {{ orderStatistic.itemCount }}
              </p>
            </div>
            <div class="order__info-item center">
              <label class="order__info-item__label">
                已訂人數
              </label>
              <p class="order__info-item__content">
                {{ orderStatistic.orderedMemberCount }}
              </p>
            </div>
            <div class="order__info-item center">
              <label class="order__info-item__label">
                未訂人數
              </label>
              <p class="order__info-item__content text-pink-500">
                {{ orderStatistic.unorderedMemberCount }}
              </p>
            </div>
          </section>
          <section id="contact" class="order__info-card min-w-[296px]">
            <div class="order__info-item no-grow">
              <label class="order__info-item__label">
                電話訂購
              </label>
              <p class="order__info-item__content">
                {{ storePhone }}
              </p>
            </div>
            <div v-if="availableOrderMethods?.length" class="order__info-item">
              <label class="order__info-item__label">
                線上訂購
              </label>
              <div class="order__info-item__content">
                <section class="flex gap-4 items-center">
                  <img
                    v-for="method in availableOrderMethods"
                    :alt="method.title"
                    :key="`method-${method.key}`"
                    :src="getOrderMethodImage(method.icon)"
                    class="my-auto">
                    <b-tooltip
                      position="is-bottom"
                      type="is-light"
                      size="is-small"
                      multilined
                      :triggers="['click']"
                      :auto-close="['outside', 'escape']">
                      <span><font-awesome-icon icon="caret-down" /></span>
                      <template v-slot:content>
                        <div
                          v-for="method in availableOrderMethods"
                          class="tooltip-content__order-method"
                          :key="`method-${method.key}`">
                          <img
                            :src="getOrderMethodImage(method.icon)"
                            :alt="`${method.title}圖示`"
                            :key="`image-${method.key}`"
                            class="min-w-[24px]">
                          <a
                            v-if="method.linkTitle"
                            target="_blank"
                            :href="orderInfo.store_order_way[method.key]">
                            {{ method.linkTitle }}
                          </a>
                          <p
                            v-else
                            :title="orderInfo.store_order_way[method.key]">
                            {{ orderInfo.store_order_way[method.key] }}
                          </p>
                        </div>
                      </template>
                    </b-tooltip>
                </section>
              </div>
            </div>
          </section>
          <section v-if="showReceiveInfo" id="receive" class="order__info-card min-w-[208px]">
            <div class="order__info-item center no-grow">
              <label class="order__info-item__label">
                已領取人數
              </label>
              <p class="order__info-item__content">
                {{ orderStatistic.received | commaFormat }}
              </p>
            </div>
            <div class="order__info-item center no-grow">
              <label class="order__info-item__label">
                未領取人數
              </label>
              <div class="order__info-item__content">
                {{ orderStatistic.unreceived | commaFormat }}
              </div>
            </div>
          </section>
        </div>
      </transition>
    </div>

    <div class="bg-white mt-4 p-4">
      <div class="flex flex-wrap gap-4 items-center justify-between mb-4">
        <b-tabs
          v-model="activeTab"
          class="mb-0 overflow-hidden overflow-x-auto"
          type="is-toggle"
        >
          <b-tab-item
            v-for="tab in tabs"
            :key="`tab-${tab.label}`"
            :label="Number.isInteger(tab.total) ? `${tab.label}(${tab.total})` : tab.label"
            :value="tab.value"
          />
        </b-tabs>
        <div class="flex flex-wrap gap-4 items-center">
          <template v-if="!isTabOrderedItems">
            <b-input
              v-model="filter"
              placeholder="搜尋人員編號、姓名"
              class="filter__input"
              @keypress.enter.native="onSearch" />
            <w-button
              size="sm"
              @click="onSearch">
              搜尋
            </w-button>
            <w-button
              class="ml-2"
              color-type="secondary"
              size="sm"
              type="link"
              @click="onReset"
            >
              清除
            </w-button>
          </template>
          <b-dropdown aria-role="list" class="export-reports">
            <template #trigger>
              <w-button
                :disabled="exportOrderMembersBtnLoading || exportBtnLoading"
                :loading="exportOrderMembersBtnLoading || exportBtnLoading"
                color-type="secondary"
                size="func"
                type="link"
              >
                <span class="flex items-center">
                  <i class="icon-cloud-download mr-2 text-base text-secondary-500" />
                  匯出報表
                </span>
              </w-button>
            </template>

            <b-dropdown-item
              aria-role="listitem"
              @click="exportStatisticsReport"
            >
              <span class="text-xs">品項統計</span>
            </b-dropdown-item>
            <b-dropdown-item
              aria-role="listitem"
              @click="exportOrdersReport"
            >
              <span class="text-xs">人員訂購列表</span>
            </b-dropdown-item>
            <b-dropdown-item
              aria-role="listitem"
              @click="exportOrdersReport(false)"
            >
              <span class="text-xs">訂購核銷列表</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <canceled-member-table
        v-if="isTabCancelledOrders"
        v-model="canceledOrders"
        :keyword="keyword" />
      <order-item-table v-if="isTabOrderedItems" :items="orderedItems" />
      <order-member-table
        v-if="isTabOrderedMembers"
        v-model="orderedMembers"
        :keyword="keyword"
        :order-id="orderId"
        :order-status="orderStatus"
      />
      <member-table
        v-if="isTabNotOrderMembers"
        :adjustable="false"
        :columns="['index', 'id', 'name', 'extension', 'mobile', 'department']"
        :keyword="keyword"
        :members="unorderedMembers"
        :selectable="false"
        :use-member-derived-departments="true"
        :warn-empty="false"
        default-select-all
        empty-text="無未訂人員"
      />
    </div>
    <copy-link-modal v-model="showUrlModal" :order="orderInfo" />
  </div>
</template>

<script>
import OrderOverview from './OrderOverview';

export default OrderOverview;
</script>

<style lang="scss" scoped>
@import './OrderOverview';
</style>
